import type { AxiosAdapter, InternalAxiosRequestConfig } from "axios";
import {
  STUB_AUTH_TOKEN,
  STUB_GLOSSARY,
  STUB_LIVEKIT_TOKEN,
  STUB_ORGANIZATION,
  STUB_RECORDING_AUDIO,
  STUB_RECORDING_VIDEO,
  STUB_ROOM,
  STUB_ROOM_2,
  STUB_USER,
} from "./stub-data";

/**
 * A custom Axios adapter used in stubbed mode (VITE_STUB=true).
 * Intercepts every request and returns pre-defined fake data without
 * making any real network calls.
 */
export const stubAdapter: AxiosAdapter = async (
  config: InternalAxiosRequestConfig
) => {
  const url = config.url ?? "";
  const method = (config.method ?? "get").toLowerCase();

  console.debug(`[stub] ${method.toUpperCase()} ${url}`);

  await new Promise((resolve) => setTimeout(resolve, 200));

  const response = <T>(data: T) => ({
    data,
    status: 200,
    statusText: "OK",
    headers: { "content-type": "application/json" },
    config,
  });

  // Auth endpoints
  if (url.includes("/v1/auth/login")) {
    return response({ user: STUB_USER });
  }
  if (url.includes("/v1/auth/sendVerificationCode")) {
    return response({ message: "Verification code sent (stub)" });
  }
  if (url.includes("/v1/auth/verifyCode")) {
    return response({ encodedToken: STUB_AUTH_TOKEN });
  }
  if (url.includes("/v1/auth/refreshToken")) {
    return response({ encodedToken: STUB_AUTH_TOKEN });
  }
  if (url.includes("/v1/auth/livekitToken")) {
    return response({ encodedToken: STUB_LIVEKIT_TOKEN });
  }
  if (url.includes("/v1/auth/subscriberToken")) {
    return response({ encodedToken: STUB_LIVEKIT_TOKEN });
  }
  if (url.includes("/v1/auth/session")) {
    return response({ message: "Session updated (stub)" });
  }

  // Room endpoints
  if (url.includes("/v1/room/list")) {
    return response({ rooms: [STUB_ROOM, STUB_ROOM_2] });
  }
  if (method === "post" && /\/v1\/room$/.test(url)) {
    return response({ room: { ...STUB_ROOM, id: crypto.randomUUID() } });
  }
  if (method === "get" && /\/v1\/room\/[^/]+$/.test(url)) {
    return response({
      room: STUB_ROOM,
      organization: STUB_ORGANIZATION,
      glossaries: [STUB_GLOSSARY],
    });
  }
  if (method === "put" && /\/v1\/room\/[^/]+$/.test(url)) {
    return response({ room: STUB_ROOM });
  }
  if (method === "delete" && /\/v1\/room\/[^/]+$/.test(url)) {
    return response({});
  }
  if (url.includes("/correctTranslation")) {
    return response({});
  }
  if (url.includes("/updateParticipant")) {
    return response({});
  }
  if (url.includes("/referral-email")) {
    return response({});
  }

  // Bot endpoints
  if (url.includes("/v1/bot/") && url.includes("/join")) {
    return response({ message: "Bot joined (stub)" });
  }
  if (url.includes("/v1/bot/") && url.includes("/status")) {
    return response({ active: false });
  }
  if (url.includes("/v1/bot/") && url.includes("/leave")) {
    return response({});
  }
  if (url.includes("/v1/bot/") && url.includes("/shareDeepLink")) {
    return response({});
  }

  // Organization endpoints
  if (method === "get" && url.includes("/v1/organization")) {
    return response({
      organization: STUB_ORGANIZATION,
      pending_invites: ["pending@example.com"],
    });
  }
  if (method === "put" && url.includes("/v1/organization")) {
    return response({ organization: STUB_ORGANIZATION });
  }
  if (url.includes("/v1/organization/inviteUser")) {
    return response({
      members: STUB_ORGANIZATION.members,
      pending_invites: [],
    });
  }
  if (url.includes("/v1/organization/removeUser")) {
    return response({
      members: STUB_ORGANIZATION.members,
      pending_invites: [],
    });
  }

  // Settings / users
  if (method === "get" && url.includes("/v1/settings/org/users/")) {
    return response({ users: [STUB_USER] });
  }
  if (method === "post" && url.includes("/v1/settings/org/users/")) {
    return response({ message: "User invited (stub)" });
  }
  if (method === "delete" && url.includes("/v1/settings/org/users/")) {
    return response({ message: "User removed (stub)" });
  }

  // Usage endpoints
  if (url.includes("/v1/usage")) {
    return response({
      organization: STUB_ORGANIZATION,
      usage: [
        {
          participant_type: "speaker",
          livekit_sid: "stub-sid-1",
          language: "en-US",
          total_seconds: 1800,
          created_at: "2026-01-20T10:00:00.000Z",
          ended_at: "2026-01-20T10:30:00.000Z",
          host_id: "stub-user-1",
          room_id: "stub-room-1",
          email: "demo@langfinity.ai",
        },
      ],
    });
  }
  if (url.includes("/v1/admin/usage")) {
    return response({ organization: STUB_ORGANIZATION, usage: [] });
  }

  // Glossary endpoints
  if (url.includes("/v1/glossary/list")) {
    return response({ glossaries: [STUB_GLOSSARY] });
  }
  if (method === "post" && /\/v1\/glossary$/.test(url)) {
    return response({
      glossary: { ...STUB_GLOSSARY, id: crypto.randomUUID() },
    });
  }
  if (method === "put" && /\/v1\/glossary\/[^/]+$/.test(url)) {
    return response({ glossary: STUB_GLOSSARY });
  }
  if (method === "delete" && /\/v1\/glossary\/[^/]+$/.test(url)) {
    return response({});
  }

  // Recording endpoints
  if (url.includes("/v1/recording/list")) {
    return response({ recordingList: [STUB_RECORDING_VIDEO, STUB_RECORDING_AUDIO] });
  }
  if (url.includes("/generate-summary")) {
    return response({});
  }
  if (url.includes("/v1/recording/start/")) {
    return response({});
  }
  if (url.includes("/v1/recording/stop/")) {
    return response({});
  }
  if (method === "get" && /\/v1\/recording\/[^/]+$/.test(url)) {
    const isAudio = url.includes(STUB_RECORDING_AUDIO.id);
    const recording = isAudio ? STUB_RECORDING_AUDIO : STUB_RECORDING_VIDEO;
    const signedUrl = isAudio ? "/stubs/stub-audio.ogg" : "/stubs/stub-video.mp4";
    return response({
      recording,
      transcript: [
        {
          id: "stub-caption-1",
          created_at: new Date("2026-01-20T10:05:00.000Z"),
          first_created: new Date("2026-01-20T10:05:00.000Z"),
          recording_id: recording.id,
          speaker_name: "Demo User",
          original_text: "Hello, this is a stub transcript.",
          // original_lang is a spoken language code (BCP-47); translations keys are text language codes
          original_lang: "en-US",
          translations: {
            en: "test",
            es: "prueba",
          },
        },
        {
          id: "stub-caption-2",
          created_at: new Date("2026-01-20T10:06:00.000Z"),
          first_created: new Date("2026-01-20T10:06:00.000Z"),
          recording_id: recording.id,
          speaker_name: "Jane Smith",
          original_text: "This is a second stub caption.",
          // original_lang is a spoken language code (BCP-47); translations keys are text language codes
          original_lang: "en-US",
          translations: {
            en: "test",
            es: "prueba",
          },
        },
      ],
      signedUrl,
      host: "Demo User",
      participants: [
        { id: "stub-user-1", name: "Demo User" },
        { id: "stub-user-2", name: "Jane Smith" },
      ],
    });
  }
  if (method === "delete" && /\/v1\/recording\/[^/]+$/.test(url)) {
    return response({});
  }
  if (method === "put" && /\/v1\/recording\/[^/]+$/.test(url)) {
    return response({});
  }

  // Chat
  if (url.includes("/v1/chat/")) {
    return response({});
  }

  // Stripe
  if (url.includes("/v1/stripe/")) {
    return response({ url: "https://example.com/stub-checkout", message: "Stripe checkout (stub)" });
  }

  // Log
  if (url.includes("/v1/log")) {
    return response({});
  }

  // Admin
  if (url.includes("/v1/admin/org/list")) {
    return response({ orgs: [{ id: "stub-org-1", email: "demo@langfinity.ai" }] });
  }

  // Internal
  if (url.includes("/v1/internal/usage/")) {
    return response([]);
  }

  // Fallback: return empty object for any unhandled route
  console.warn(`[stub] Unhandled route: ${method.toUpperCase()} ${url}`);
  return response({});
};
